import React, { useMemo, useState } from "react"
import PropTypes from "prop-types"
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from "recharts"
import { formatCurrency, formatNumber, formatPercent, colorPara } from "../helpers"

const TooltipContent = ({ active, payload }) => {
  if (!active || !payload || !payload[0]) return null
  const d = payload[0].payload
  return (
    <div style={{ background: "#0f172a", color: "#fff", padding: "8px 12px", borderRadius: 8, fontSize: 12, maxWidth: 280 }}>
      <div style={{ fontWeight: 700, marginBottom: 4 }}>{d.nombre}</div>
      <div>{formatCurrency(d.valor)}</div>
      <div style={{ color: "#94a3b8", fontSize: 11 }}>
        {formatNumber(d.transacciones)} transacciones · {formatPercent(d.pct)}
      </div>
    </div>
  )
}

TooltipContent.propTypes = { active: PropTypes.bool, payload: PropTypes.array }

const TopBottomBlock = ({ titulo, items, color, icon }) => {
  if (!items || items.length === 0) return null
  return (
    <div style={{ flex: "1 1 380px", background: "#fff", borderRadius: 14, padding: 20, border: "1px solid #e2e8f0", minWidth: 0 }}>
      <h6 style={{ margin: 0, color: "#0f172a", fontWeight: 600, marginBottom: 12 }}>
        <i className={`mdi mdi-${icon} me-2`} style={{ color }} />
        {titulo}
      </h6>
      <div style={{ width: "100%", height: Math.max(220, items.length * 36) }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={items} layout="vertical" margin={{ top: 5, right: 30, left: 0, bottom: 5 }}>
            <XAxis type="number" tick={{ fontSize: 11, fill: "#64748b" }} tickFormatter={v => formatCurrency(v)} />
            <YAxis
              type="category" dataKey="nombre" width={170}
              tick={{ fontSize: 11, fill: "#475569" }} interval={0}
              tickFormatter={s => (s && s.length > 24 ? `${s.slice(0, 23)}…` : s)}
            />
            <Tooltip content={<TooltipContent />} cursor={{ fill: "rgba(59,130,246,0.06)" }} />
            <Bar dataKey="valor" radius={[0, 6, 6, 0]}>
              {items.map((d, i) => <Cell key={i} fill={color} fillOpacity={0.4 + 0.6 * (i / items.length)} />)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  )
}

TopBottomBlock.propTypes = { titulo: PropTypes.string, items: PropTypes.array, color: PropTypes.string, icon: PropTypes.string }

// Tabla agregada sucursal × método (suma sobre todos los días)
const SucursalesTab = ({ data }) => {
  const [busqueda, setBusqueda] = useState("")
  const [orden, setOrden] = useState({ campo: "valor", asc: false })

  const topBottom = data?.top_bottom || {}
  const filas = data?.sucursal_metodos || []

  // Agrupar sucursal × método (sumar días)
  const agregadas = useMemo(() => {
    const map = new Map()
    filas.forEach(f => {
      const key = `${f.sucursal}|${f.metodo_pago}`
      const prev = map.get(key) || {
        sucursal: f.sucursal,
        metodo: f.metodo_pago,
        transacciones: 0,
        valor: 0,
      }
      prev.transacciones += Number(f.transacciones) || 0
      prev.valor += Number(f.total_valor) || 0
      map.set(key, prev)
    })
    return Array.from(map.values())
  }, [filas])

  const filtradas = useMemo(() => {
    const q = busqueda.toLowerCase()
    const base = q
      ? agregadas.filter(a =>
          (a.sucursal || "").toLowerCase().includes(q) ||
          (a.metodo || "").toLowerCase().includes(q),
        )
      : agregadas
    const dir = orden.asc ? 1 : -1
    return [...base].sort((a, b) => {
      const av = a[orden.campo]
      const bv = b[orden.campo]
      if (typeof av === "string") return av.localeCompare(bv) * dir
      return ((av || 0) - (bv || 0)) * dir
    })
  }, [agregadas, busqueda, orden])

  const toggleOrden = campo => {
    setOrden(prev => prev.campo === campo ? { campo, asc: !prev.asc } : { campo, asc: false })
  }

  const TH = ({ campo, children, align = "left", width }) => {
    const activo = orden.campo === campo
    return (
      <th
        onClick={() => toggleOrden(campo)}
        style={{
          padding: "10px 12px", textAlign: align, fontSize: 11, fontWeight: 600,
          color: activo ? "#0f172a" : "#475569",
          textTransform: "uppercase", letterSpacing: 0.4,
          borderBottom: "1px solid #e2e8f0", background: "#f1f5f9",
          cursor: "pointer", userSelect: "none",
          position: "sticky", top: 0, zIndex: 1,
          width,
        }}
      >
        {children}
        <i
          className={`mdi mdi-${activo ? (orden.asc ? "chevron-up" : "chevron-down") : "unfold-more-horizontal"} ms-1`}
          style={{ opacity: activo ? 1 : 0.4, fontSize: 14 }}
        />
      </th>
    )
  }

  TH.propTypes = { campo: PropTypes.string, children: PropTypes.node, align: PropTypes.string, width: PropTypes.any }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {/* Top + Bottom */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
        <TopBottomBlock titulo="Top 10 sucursales" items={topBottom.top_sucursales} color="#10b981" icon="trophy" />
        <TopBottomBlock titulo="Bottom 10 sucursales" items={topBottom.bottom_sucursales} color="#ef4444" icon="trending-down" />
      </div>

      {/* Tabla agregada */}
      <div style={{ background: "#fff", borderRadius: 14, padding: 20, border: "1px solid #e2e8f0" }}>
        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
          <h6 style={{ margin: 0, color: "#0f172a", fontWeight: 600 }}>
            <i className="mdi mdi-table me-2 text-primary" />
            Sucursal × Método ({filtradas.length})
          </h6>
          <div style={{ position: "relative", maxWidth: 280 }}>
            <i className="mdi mdi-magnify" style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "#94a3b8" }} />
            <input
              type="text"
              className="form-control form-control-sm"
              placeholder="Buscar sucursal o método…"
              value={busqueda}
              onChange={e => setBusqueda(e.target.value)}
              style={{ paddingLeft: 32, minWidth: 240 }}
            />
          </div>
        </div>

        <div style={{ border: "1px solid #e2e8f0", borderRadius: 8, overflow: "hidden" }}>
          <div style={{ maxHeight: 540, overflowY: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr>
                  <TH campo="sucursal">Sucursal</TH>
                  <TH campo="metodo">Método</TH>
                  <TH campo="transacciones" align="right">Transacciones</TH>
                  <TH campo="valor" align="right">Valor</TH>
                </tr>
              </thead>
              <tbody>
                {filtradas.length === 0 && (
                  <tr><td colSpan={4} style={{ padding: 40, textAlign: "center", color: "#64748b" }}>Sin filas para mostrar.</td></tr>
                )}
                {filtradas.map((a, i) => (
                  <tr key={i} style={{ borderBottom: "1px solid #f1f5f9" }}>
                    <td style={{ padding: "8px 12px", color: "#0f172a", fontWeight: 500 }}>
                      <i className="mdi mdi-store text-primary me-2" />
                      {a.sucursal}
                    </td>
                    <td style={{ padding: "8px 12px", color: "#475569" }}>
                      <span style={{ display: "inline-block", width: 8, height: 8, borderRadius: "50%", background: colorPara(i), marginRight: 8 }} />
                      {a.metodo}
                    </td>
                    <td style={{ padding: "8px 12px", textAlign: "right", color: "#475569" }}>{formatNumber(a.transacciones)}</td>
                    <td style={{ padding: "8px 12px", textAlign: "right", color: "#0f172a", fontWeight: 600 }}>{formatCurrency(a.valor)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  )
}

SucursalesTab.propTypes = { data: PropTypes.object }

export default SucursalesTab
